// Copyright 2022 Carnegie Mellon University. All Rights Reserved.
// Released under a MIT (SEI)-style license. See LICENSE.md in the
// project root for license information.
import { ChangeDetectorRef, Component, Input, OnDestroy, ViewChild, ElementRef, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatSort, MatSortable, Sort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { Subject, Observable } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import {
  ComnSettingsService,
} from '@cmusei/crucible-common';
import { UserDataService } from 'src/app/data/user/user-data.service';
import { UserQuery, CurrentUserQuery } from 'src/app/data/user/user.query';
import { SignalRService } from 'src/app/services/signalr.service';
import { MselDataService, MselPlus } from 'src/app/data/msel/msel-data.service';
import { MselQuery } from 'src/app/data/msel/msel.query';
import { CrucibleDialogService } from '@cmusei/crucible-common';
import { MatDialog } from '@angular/material/dialog';
import { MselImportDialogComponent } from 'src/app/components/msel-import-dialog/msel-import-dialog.component';
import { UIDataService } from 'src/app/data/ui/ui-data.service';
import { User, SystemPermission } from 'src/app/generated/blueprint.api';
import { MselItemStatus } from 'src/app/generated/blueprint.api';
import { PermissionDataService } from 'src/app/data/permission/permission-data.service';

@Component({
  selector: 'app-msel-list',
  templateUrl: './msel-list.component.html',
  styleUrls: ['./msel-list.component.scss'],
  standalone: false
})
export class MselListComponent implements OnDestroy, OnInit {
  @Input() loggedInUserId: string;
  @Input() canAccessAdminSection: boolean;
  @ViewChild('jsonInput') jsonInput: ElementRef<HTMLInputElement>;
  @ViewChild('xlsxInput') xlsxInput: ElementRef<HTMLInputElement>;
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild(MatPaginator) set paginator(paginator: MatPaginator) {
    if (paginator && this.mselDataSource) {
      this.mselDataSource.paginator = paginator;
    }
  }
  mselList: MselPlus[] = [];
  isReady = false;
  uploadProgress = 0;
  uploadMselId = '';
  fileType = '';
  filteredMselList: MselPlus[] = [];
  filterControl = new UntypedFormControl();
  filterString = '';
  sortedMselList: MselPlus[] = [];
  defaultTab = 'Info';
  isLoading = true;
  areButtonsDisabled = false;
  mselDataSource: MatTableDataSource<MselPlus>;
  displayedColumns: string[] = [
    'action',
    'name',
    'description',
    'isTemplate',
    'status',
    'dateCreated',
    'dateModified',
  ];
  imageFilePath = '';
  userList: User[] = [];
  currentUserName = '';
  selectedMselType = 'all';
  selectedMselStatus = 'all';
  itemStatus = [
    MselItemStatus.Pending,
    MselItemStatus.Entered,
    MselItemStatus.Approved,
    MselItemStatus.Complete,
    MselItemStatus.Deployed,
    MselItemStatus.Archived,
  ];
  allMselsAreLoaded = false;
  private unsubscribe$ = new Subject();

  constructor(
    public dialogService: CrucibleDialogService,
    private router: Router,
    private userDataService: UserDataService,
    private userQuery: UserQuery,
    private currentUserQuery: CurrentUserQuery,
    private settingsService: ComnSettingsService,
    private mselDataService: MselDataService,
    private mselQuery: MselQuery,
    private signalRService: SignalRService,
    private uiDataService: UIDataService,
    private permissionDataService: PermissionDataService,
    private changeDetectorRef: ChangeDetectorRef,
    private dialog: MatDialog
  ) {
    // Initial datasource
    this.mselDataSource = new MatTableDataSource<MselPlus>(
      new Array<MselPlus>()
    );
  }

  ngOnInit() {
    // Load permissions, then load MSELs based on access level
    this.permissionDataService.load()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        if (this.permissionDataService.hasPermission(SystemPermission.ViewMsels)) {
          this.mselDataService.load();
          this.allMselsAreLoaded = true;
        } else {
          this.mselDataService.loadMine();
        }
        this.changeDetectorRef.markForCheck();
      });
    // subscribe to users
    this.userQuery.selectAll()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((users) => {
        this.userList = users;
      });
    // load the users
    this.userDataService.load().pipe(take(1)).subscribe();
    // Subscribe to current user
    this.currentUserQuery
      .select()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((cu) => {
        this.currentUserName = cu.name;
      });
    this.sort.sort(<MatSortable>{ id: 'name', start: 'asc' });
    this.mselDataSource.sort = this.sort;
    // subscribe to MSELs loading
    this.mselQuery
      .selectLoading()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((isLoading) => {
        this.isReady = !isLoading;
        this.areButtonsDisabled = isLoading;
      });
    // subscribe to MSELs loading progress
    this.mselDataService.uploadProgress
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((uploadProgress) => {
        this.uploadProgress = uploadProgress;
      });
    // subscribe to MSELs
    (this.mselQuery.selectAll() as Observable<MselPlus[]>)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((msels) => {
        const mselPlusList: MselPlus[] = [];
        msels.forEach((msel) => {
          const mselPlus = new MselPlus();
          Object.assign(mselPlus, msel);
          mselPlusList.push(mselPlus);
        });
        this.mselList = mselPlusList;
        this.filterMsels();
        this.isLoading = false;
        this.areButtonsDisabled = false;
      });
    // subscribe to filter control changes
    this.filterControl.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((term) => {
        this.filterString = term;
        this.filterMsels();
      });
    // set image
    this.imageFilePath = this.settingsService.settings.AppTopBarImage.replace(
      'white',
      'blue'
    );
    // remove case sensitivity from mat-table sort
    this.mselDataSource.sortingDataAccessor = (
      data: any,
      sortHeaderId: string
    ): string => {
      if (typeof data[sortHeaderId] === 'string') {
        return data[sortHeaderId].toLocaleLowerCase();
      }
      return data[sortHeaderId];
    };
  }

  openMsel(mselId) {
    // join signalR for this MSEL
    this.signalRService.selectMsel(mselId);
    this.uiDataService.setMselTab(this.defaultTab);
  }

  uploadFile(fileType: string, mselId: string) {
    this.areButtonsDisabled = true;
    this.uploadMselId = mselId ? mselId : '';
    this.fileType = fileType;
  }

  /**
   * Selects the file(s) to be uploaded. Called when file selection is changed
   */
  selectFile(e) {
    const file = e.target.files[0];
    if (!file) {
      this.areButtonsDisabled = false;
      return;
    }
    this.uploadProgress = 0;
    this.isReady = false;
    if (this.fileType === 'xlsx') {
      this.mselDataService.uploadXlsx(
        this.uploadMselId,
        file,
        'events',
        true
      );
    } else {
      this.isReady = true;
      this.importJson(file);
    }
    this.jsonInput.nativeElement.value = null;
    this.xlsxInput.nativeElement.value = null;
  }

  importJson(file: File) {
    this.dialog
      .open(MselImportDialogComponent, {
        data: { file },
        width: '600px',
        maxWidth: '90vw',
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((mselId) => {
        this.areButtonsDisabled = false;
        if (mselId) {
          this.openMsel(mselId);
          this.router.navigate(['/build'], { queryParams: { msel: mselId } });
        }
      });
  }

  /**
   * Trigger a download for a file. This will open the file in the broswer if it is an image or pdf
   *
   * @param mselId: The GUID of the file to download
   * @param name: The name to use when triggering the download
   */
  downloadXlsxFile(msel: MselPlus) {
    this.isReady = false;
    this.mselDataService.downloadXlsx(msel.id).subscribe(
      (data) => {
        const url = window.URL.createObjectURL(data);
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.download = msel.name.endsWith('.xlsx')
          ? msel.name
          : msel.name + '.xlsx';
        link.click();
        this.isReady = true;
      },
      (err) => {
        this.isReady = true;
        window.alert('Error downloading file');
      },
      () => {
        this.isReady = true;
      }
    );
  }

  /**
   * Trigger a download for a file. This will open the file in the broswer if it is an image or pdf
   *
   * @param mselId: The GUID of the file to download
   * @param name: The name to use when triggering the download
   */
  downloadJsonFile(msel: MselPlus) {
    this.isReady = false;
    this.mselDataService.downloadJson(msel.id).subscribe(
      (data) => {
        const url = window.URL.createObjectURL(data);
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.download = msel.name + '-msel.json';
        link.click();
        this.isReady = true;
      },
      (err) => {
        this.isReady = true;
        window.alert('Error downloading file');
      },
      () => {
        this.isReady = true;
      }
    );
  }

  addMsel() {
    this.areButtonsDisabled = true;
    this.mselDataService
      .add({
        name: 'New MSEL',
        description:
          'Created from Default Settings by ' + this.currentUserName,
        status: 'Pending',
        dataFields: this.settingsService.settings.DefaultDataFields,
      })
      .pipe(take(1))
      .subscribe((msel) => {
        this.areButtonsDisabled = false;
        // Open the newly created MSEL in the edit page
        this.openMsel(msel.id);
        this.router.navigate(['/build'], {
          queryParams: { msel: msel.id },
        });
      });
  }

  copyMsel(mselId: string): void {
    const msel = this.mselList.find(m => m.id === mselId);
    if (!msel) return;

    this.dialogService
      .confirm({ title: 'Copy MSEL', message: 'Are you sure that you want to copy ' + msel.name + '?'
       }).afterClosed().subscribe((result) => {
        if (result) {
          this.mselDataService.copy(msel.id);
        }
      });
  }

  delete(msel: MselPlus): void {
    this.dialogService
      .confirm({ title: 'Delete MSEL', message: 'Are you sure that you want to delete ' + msel.name + '?'
       }).afterClosed().subscribe((result) => {
        if (result) {
          this.mselDataService.delete(msel.id);
        }
      });
  }

  filterMsels() {
    let filteredMselList = this.mselList;
    switch (this.selectedMselType) {
      case 'is':
        filteredMselList = filteredMselList.filter((m) => m.isTemplate);
        break;
      case 'is not':
        filteredMselList = filteredMselList.filter((m) => !m.isTemplate);
        break;
      default:
        filteredMselList = filteredMselList;
        break;
    }
    switch (this.selectedMselStatus) {
      case 'Pending':
        filteredMselList = filteredMselList.filter(
          (m) => m.status.toString() === 'Pending'
        );
        break;
      case 'Entered':
        filteredMselList = filteredMselList.filter(
          (m) => m.status.toString() === 'Entered'
        );
        break;
      case 'Approved':
        filteredMselList = filteredMselList.filter(
          (m) => m.status.toString() === 'Approved'
        );
        break;
      case 'Deployed':
        filteredMselList = filteredMselList.filter(
          (m) => m.status.toString() === 'Deployed'
        );
        break;
      case 'Completed':
        filteredMselList = filteredMselList.filter(
          (m) => m.status.toString() === 'Completed'
        );
        break;
      default:
        filteredMselList = filteredMselList;
        break;
    }
    this.mselDataSource.data = filteredMselList;
    const filterValue = this.filterString.toLowerCase().trim(); //  Remove whitespace and MatTableDataSource defaults to lowercase matches
    this.mselDataSource.filter = filterValue;
  }

  goToUrl(url): void {
    this.router.navigate([url], {
      queryParamsHandling: 'merge',
    });
  }

  getUserName(userId: string) {
    const user = this.userList.find((u) => u.id === userId);
    return user ? user.name : 'System';
  }

  adminLoadAllMsels() {
    // load the MSELs
    this.mselDataService.load();
    this.allMselsAreLoaded = true;
  }

  adminLoadMyMsels() {
    this.mselDataService.loadMine();
    this.allMselsAreLoaded = false;
  }

  canCreateMsels(): boolean {
    return this.permissionDataService.hasPermission(SystemPermission.CreateMsels);
  }

  canViewMsel(msel: MselPlus): boolean {
    return this.permissionDataService.hasPermission(SystemPermission.ViewMsels) ||
      msel?.hasRole(this.loggedInUserId, '').editor ||
      (msel?.isTemplate && this.permissionDataService.hasPermission(SystemPermission.CreateMsels));
  }

  canEditMsel(msel: MselPlus): boolean {
    return this.permissionDataService.hasPermission(SystemPermission.EditMsels) ||
      msel?.hasRole(this.loggedInUserId, '').editor;
  }

  canManageMsel(msel: MselPlus): boolean {
    return this.permissionDataService.hasPermission(SystemPermission.ManageMsels) ||
      msel?.hasRole(this.loggedInUserId, '').owner;
  }

  getDeleteTooltip(msel: MselPlus): string {
    if (!this.isReady) {
      return 'System is not ready';
    }
    if (!this.canManageMsel(msel)) {
      return 'You do not have permission to delete this MSEL';
    }
    if (msel.status === 'Deployed') {
      return 'Cannot delete deployed MSEL';
    }
    if (msel.isTemplate) {
      return 'Cannot delete template MSELs';
    }
    return 'Delete ' + msel.name;
  }

  isValidDate(date: Date | null | undefined): boolean {
    if (!date) return false;
    const year = new Date(date).getFullYear();
    // Check for null, undefined, or invalid dates like 1969 (Unix epoch default)
    return year > 1970;
  }

  ngOnDestroy() {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }
}
